<template>
  <div>
    <div class="back-link mb-3" @click="backTapped">
      <span class="material-icons">arrow_left</span>
      <span>{{ $t('shared.back') }}</span>
    </div>

    <h2 class="auth-heading">{{ $t('on_boarding.email_sign_up_details_page.create_your_account') }}</h2>

    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      {{ errorMessage }}
    </div>

    <form @submit.prevent="signUp">
      <div class="pro-field mb-3">
        <label class="pro-field__label" for="signupEmail">{{ $t('shared.email') }}</label>
        <input
          id="signupEmail"
          v-model.trim="email"
          type="email"
          class="form-control form-control-lg pro-field__input"
          placeholder="example@email.com"
          autocomplete="email"
          required
        >
      </div>

      <div class="pro-field mb-3">
        <label class="pro-field__label" for="signupName">{{ $t('on_boarding.email_sign_up_details_page.your_name') }}</label>
        <input
          id="signupName"
          v-model="fullName"
          type="text"
          class="form-control form-control-lg pro-field__input"
          :placeholder="$t('on_boarding.email_sign_up_details_page.first_and_last_name')"
          autocomplete="name"
          required
        >
        <small class="pro-field__hint">
          {{ $t('on_boarding.email_sign_up_details_page.we_ll_set_your_display_name') }}
        </small>
      </div>

      <!-- Single password field with show/hide toggle. We've intentionally
           dropped the confirm-password field — show/hide solves the typo
           problem with less friction. -->
      <div class="pro-field mb-4">
        <label class="pro-field__label" for="signupPassword">{{ $t('on_boarding.email_sign_up_details_page.password') }}</label>
        <div class="password-input">
          <input
            id="signupPassword"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            class="form-control form-control-lg pro-field__input"
            :placeholder="$t('on_boarding.email_sign_up_details_page.at_least_6_characters')"
            autocomplete="new-password"
            minlength="6"
            required
          >
          <button
            type="button"
            class="password-toggle"
            :aria-label="showPassword ? $t('on_boarding.email_sign_up_details_page.hide_password') : $t('on_boarding.email_sign_up_details_page.show_password')"
            @click="showPassword = !showPassword"
          >
            <span class="material-icons">{{ showPassword ? 'visibility_off' : 'visibility' }}</span>
          </button>
        </div>
      </div>

      <button
        type="submit"
        class="btn pro-btn pro-btn--accent w-100"
        :disabled="isLoading || !canSubmit"
      >
        <template v-if="isLoading">
          <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          <span>{{ $t('on_boarding.email_sign_up_details_page.creating_account') }}</span>
        </template>
        <template v-else>
          <span class="material-icons">check_circle</span>
          <span>{{ $t('on_boarding.email_sign_up_details_page.create_account') }}</span>
        </template>
      </button>
    </form>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'OnBoardingEmailSignUpDetailsPage',
  data() {
    return {
      email: '',
      password: '',
      fullName: '',
      showPassword: false,
    };
  },
  computed: {
    isLoading(): boolean {
      return this.$store.state.onBoarding.isLoading;
    },
    errorMessage(): string | null {
      return this.$store.state.onBoarding.errorMessage;
    },
    canSubmit(): boolean {
      return this.email.length > 0
        && this.password.length >= 6
        && this.fullName.trim().length > 0;
    },
  },
  methods: {
    backTapped() {
      this.$store.commit('onBoarding/setError', null);
      this.$store.commit('onBoarding/popPage');
    },
    async signUp() {
      if (!this.canSubmit) {
        this.$store.commit('onBoarding/setError', this.$t('on_boarding.email_sign_up_details_page.fill_in_all_fields'));
        return;
      }
      await this.$store.dispatch('onBoarding/signUpWithEmail', {
        email: this.email,
        password: this.password,
        fullName: this.fullName.trim(),
      });
    },
  },
  beforeUnmount() {
    this.$store.commit('onBoarding/setError', null);
  },
});
</script>

<style scoped>
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #a1a4ad;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
}
.back-link:hover {
  color: #fff;
}

.auth-heading {
  font-family: 'Montserrat', Helvetica, Arial, sans-serif;
  font-size: clamp(1.4rem, 2.4vw, 1.75rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: #fff;
  margin: 0 0 1.25rem;
}

/* Password show/hide affordance — sits inside the rounded `pro-field__input`. */
.password-input {
  position: relative;
}
.password-toggle {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.55);
  cursor: pointer;
  padding: 6px;
  display: flex;
  align-items: center;
  border-radius: 8px;
}
.password-toggle:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.06);
}
.password-toggle .material-icons {
  font-size: 20px;
}
</style>
